import { Component } from '@theme/component';

/**
 * @typedef {Object} InlineArticlesRefs
 * @property {HTMLElement[]} articles
 */

class InlineArticles extends Component {
  /** @type {IntersectionObserver | null} */
  #observer = null;

  /** @type {Map<string, string>} */
  #cache = new Map();

  /** @type {WeakMap<HTMLElement, Promise<void>>} */
  #inFlightLoads = new WeakMap();

  /** @type {string | null} */
  #hashTargetId = null;

  /** @type {boolean} */
  #isAligningHashTarget = false;

  connectedCallback() {
    super.connectedCallback();

    const articles = /** @type {HTMLElement[]} */ (this.refs.articles);
    if (!Array.isArray(articles)) return;

    this.#hashTargetId = this.#getHashTargetId();

    this.#observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (!entry.isIntersecting) continue;
          this.#observer?.unobserve(entry.target);
          this.#loadArticle(/** @type {HTMLElement} */ (entry.target));
        }
      },
      { rootMargin: '500px 0px' }
    );

    for (const article of articles) {
      this.#observer.observe(article);
    }

    this.#alignHashTarget();
    this.#hydrateHashTarget(articles);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#observer?.disconnect();
  }

  /**
   * @param {HTMLElement} placeholder
   */
  async #loadArticle(placeholder) {
    if (!placeholder.hasAttribute('data-loading')) return;

    const inFlight = this.#inFlightLoads.get(placeholder);
    if (inFlight) {
      await inFlight;
      return;
    }

    const loadPromise = this.#loadArticleContent(placeholder);
    this.#inFlightLoads.set(placeholder, loadPromise);

    try {
      await loadPromise;
    } finally {
      this.#inFlightLoads.delete(placeholder);
    }
  }

  /**
   * @param {HTMLElement} placeholder
   */
  async #loadArticleContent(placeholder) {
    const url = placeholder.dataset.articleUrl;
    if (!url) return;

    const cached = this.#cache.get(url);
    if (cached) {
      this.#renderContent(placeholder, cached);
      return;
    }

    try {
      const response = await fetch(url);
      const html = await response.text();
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const main = doc.getElementById('MainContent');

      if (!main) return;

      const content = main.innerHTML;
      this.#cache.set(url, content);
      this.#renderContent(placeholder, content);
    } catch {
      // Silently fail — placeholder remains with skeleton
    }
  }

  /**
   * Ensures deep links to later articles stay aligned after lazy content injection.
   * @param {HTMLElement[]} articles
   */
  async #hydrateHashTarget(articles) {
    const targetId = this.#hashTargetId;
    if (!targetId) return;

    const target = document.getElementById(targetId);
    if (!(target instanceof HTMLElement)) return;

    const targetIndex = articles.indexOf(target);
    if (targetIndex === -1) return;

    for (let i = 0; i <= targetIndex; i += 1) {
      const article = articles[i];
      if (!article) continue;
      this.#observer?.unobserve(article);
      await this.#loadArticle(article);
      this.#alignHashTarget();
    }

    this.#alignHashTarget();
  }

  /**
   * @returns {string | null}
   */
  #getHashTargetId() {
    const hash = window.location.hash;
    if (!hash) return null;

    const targetId = decodeURIComponent(hash.replace(/^#/, '').replace(/^\/+/, ''));
    return targetId || null;
  }

  #alignHashTarget() {
    if (!this.#hashTargetId || this.#isAligningHashTarget) return;

    const target = document.getElementById(this.#hashTargetId);
    if (!(target instanceof HTMLElement)) return;

    this.#isAligningHashTarget = true;

    requestAnimationFrame(() => {
      target.scrollIntoView({ behavior: 'instant', block: 'start' });
      this.#isAligningHashTarget = false;

      if (!this.#hasPendingLoadsThroughTarget(target)) {
        this.#hashTargetId = null;
      }
    });
  }

  /**
   * @param {HTMLElement} target
   * @returns {boolean}
   */
  #hasPendingLoadsThroughTarget(target) {
    const articles = /** @type {HTMLElement[]} */ (this.refs.articles);
    if (!Array.isArray(articles)) return true;

    const targetIndex = articles.indexOf(target);
    if (targetIndex === -1) return true;

    for (let i = 0; i <= targetIndex; i += 1) {
      const article = articles[i];
      if (article?.hasAttribute('data-loading')) return true;
    }

    return false;
  }

  /**
   * @param {HTMLElement} placeholder
   * @param {string} html
   */
  #renderContent(placeholder, html) {
    const container = placeholder.querySelector('[data-content]');
    if (!container) return;

    container.innerHTML = html;
    placeholder.removeAttribute('data-loading');
    this.#alignHashTarget();
  }
}

customElements.define('inline-articles', InlineArticles);
